/* Připomínka blížící se týmové akce pro zvoneček (NotificationCenter).
 *
 * PROČ TOHLE EXISTUJE
 * -------------------
 * Manažer založí poradu na příští úterý, akce se propíše poradci do diáře — a
 * poradce se o ní dozví, jen když si diář sám otevře a dolistuje na ten den.
 * Zvoneček uměl jen „po termínu" a „dnes" (budoucí položky zahazoval), takže
 * upozornění PŘED akcí neexistovalo.
 *
 * PROČ SE NESTAVÍ DRUHÝ NOTIFIKAČNÍ SYSTÉM
 * ----------------------------------------
 * Zvoneček počítá všechno v prohlížeči z /api/crm/tasks (vlastní diář). Zrcadlo
 * týmové akce tam už je — nese `source: "from_team_event"` i `pripominka_dny`.
 * Stačí tedy přidat čtvrtou skupinu, ne nový backend, cron ani push kanál.
 *
 * CO TO NEDĚLÁ (a je potřeba to říkat nahlas)
 * ------------------------------------------
 * Nic se neodesílá — žádný e-mail, SMS ani push. Připomínku uvidí jen ten, kdo
 * si aplikaci otevře. `reminder_min` (pole v diáři, „15 min předem") je dodnes
 * mrtvé: ukládá se, ale nikdo ho nečte a iCal feed z něj VALARM nedělá. Vědomě
 * se na něj NEVĚŠÍME — slibovalo by to připomínku, kterou nic nespouští.
 *
 * ZÁMĚRNĚ JEN TÝMOVÉ AKCE
 * -----------------------
 * Filtr `source === "from_team_event"` je úzký schválně. Pustit do zvonečku
 * všechny budoucí schůzky by změnilo chování celého diáře (cizí proud) a
 * poradci s plným kalendářem by zvoneček zahltilo.
 */

/** Značka zrcadla týmové akce v diáři (backend: routes/team_akce.AKCE_SOURCE). */
export const AKCE_SOURCE = "from_team_event";

/** Kolik dní předem připomínat, když akce hodnotu nenese (starý záznam). */
export const PRIPOMINKA_DEFAULT_DNI = 2;

/** Strop shodný s backendem (routes/team_akce.PRIPOMINKA_MAX_DNI). */
export const PRIPOMINKA_MAX_DNI = 14;

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/**
 * Počet celých dní od `ted` do zadaného ISO termínu. `null` = termín nejde
 * přečíst — volající pak položku vynechá, místo aby si den domyslel.
 */
export function dnuDo(iso, ted = new Date()) {
  if (typeof iso !== "string" || !iso) return null;
  const cil = new Date(iso);
  if (Number.isNaN(cil.getTime())) return null;
  const zaklad = new Date(ted);
  if (Number.isNaN(zaklad.getTime())) return null;
  return Math.round((startOfDay(cil) - startOfDay(zaklad)) / 86400000);
}

/**
 * Kolik dní předem tuhle položku připomínat. Mimo rozsah 0–14, nečíslo nebo
 * chybějící hodnota → default. `0` je platná volba („až v den akce"), takže se
 * NESMÍ spolknout falsy testem.
 */
export function pripominkaDnu(task) {
  const v = task?.pripominka_dny;
  if (typeof v !== "number" || !Number.isFinite(v)) return PRIPOMINKA_DEFAULT_DNI;
  const cele = Math.trunc(v);
  if (cele < 0 || cele > PRIPOMINKA_MAX_DNI) return PRIPOMINKA_DEFAULT_DNI;
  return cele;
}

/**
 * Vybere z diáře týmové akce, které je čas připomenout.
 *
 * Pravidla (každé kvůli konkrétní pasti):
 *  • jen `source === "from_team_event"` — cizí položky diáře patří jinam,
 *  • hotové (`done`) se nepřipomínají,
 *  • termín se čte `deadline || start_iso` — STEJNÉ pořadí jako ve zbytku
 *    zvonečku, ať položka nespadne do dvou skupin s různým dnem,
 *  • `dd <= 0` se přeskočí — dnešek řeší skupina „Dnes" a minulost „Po termínu";
 *    jinak by ta samá porada visela ve zvonečku dvakrát,
 *  • `dd > pripominkaDnu(task)` se přeskočí — porada za měsíc ještě není zpráva,
 *  • nečitelný termín se přeskočí (žádné „asi zítra").
 *
 * Vrací pole { id, task, days, when, druh, text } seřazené od nejbližší akce.
 */
export function nadchazejiciAkce(tasks, ted = new Date()) {
  if (!Array.isArray(tasks)) return [];
  const out = [];
  for (const tk of tasks) {
    if (!tk || typeof tk !== "object") continue;
    if (tk.source !== AKCE_SOURCE) continue;
    if (tk.done) continue;
    const iso = tk.deadline || tk.start_iso;
    const dd = dnuDo(iso, ted);
    if (dd === null || dd <= 0) continue;
    if (dd > pripominkaDnu(tk)) continue;
    const when = new Date(iso);
    out.push({
      id: tk.id,
      task: tk,
      days: dd,
      when,
      druh: tk.team_event_druh || null,
      text: tk.text || "",
    });
  }
  out.sort((a, b) => a.when - b.when);
  return out;
}

/* Barva chipu podle druhu akce. Sladěno s lib/manazer/akceModel.DRUHY, ale
   držené zvlášť: zvoneček je v app-shellu na každé stránce a nemá si tahat
   manažerský modul kvůli třem barvám. */
const BARVA_DRUHU = {
  porada: "var(--accent)",
  teambuilding: "var(--positive, #4a7c59)",
  skoleni: "var(--warn, #c2853a)",
};

export const barvaDruhu = (druh) => BARVA_DRUHU[druh] || "var(--accent)";

export default nadchazejiciAkce;
